import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { OrganizationService } from '@/api';
import { Button } from '@/components/ui/button';
import { useStore } from '@nanostores/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { file2Base64 } from '@/utils/file2Base64';

type UpdateOrgLogoDialogProps = {
  orgId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
};

export function UpdateOrgLogoDialog({ orgId, open, setOpen }: UpdateOrgLogoDialogProps) {
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const orgLogoUrls = useStore($orgLogoUrls);
  const orgLogoUrl = orgLogoUrls[orgId];

  // Fetch organization details
  const { data: orgData, isLoading: isOrgLoading, isError } = useQuery({
    queryKey: ['organization', orgId],
    queryFn: async () => {
      const res = await OrganizationService.getOrganizations();
      return res.find((org) => org.id === orgId); // Ensure you get the right organization
    },
    enabled: open, // Fetch when dialog is open
  });

  // Ensure the organization logo URL is up-to-date
  useEffect(() => {
    if (open) {
      ensureOrgLogoUrl(orgId);
    }
  }, [open, orgId]);

  const updateOrgLogo = useMutation({
    mutationFn: async (payload: { logoBase64: string; logoFileName: string }) => {
      // Ensure you send the required fields as per UpdateOrgRequest type
      const updatePayload = {
        name: orgData?.name || '', // Ensure organization name is included
        logoBase64: payload.logoBase64, // Base64 encoded logo
        logoFileName: payload.logoFileName, // Logo file name
      };

      return await OrganizationService.updateOrganization(orgId, updatePayload); // Updated payload
      // console.log(updatePayload);
    },
    onSuccess: () => {
      toast.success('Organization logo updated successfully');
      ensureOrgLogoUrl(orgId); // Refresh the logo URL after update
      setOpen(false);
    },
    onError: (error: any) => {
      console.error(error);
      toast.error('Failed to update organization logo');
    },
  });

  // Handle form submission
  const handleSubmit = async () => {
    if (!logoFile) {
      toast.error('Please select a logo file');
      return;
    }

    try {
      // console.log('Converting file to base64...'); // Add this line
      const logoBase64 = await file2Base64(logoFile); // Convert file to base64
      // console.log('File converted to base64:', logoBase64); // Add this line
      updateOrgLogo.mutate({ logoBase64, logoFileName: logoFile.name });
      evictOrgLogoUrl(orgId);
    } catch (error) {
      console.error('Error encoding file:', error);
      toast.error('Failed to process logo file');
    }
  };

  if (isOrgLoading) return <div>Loading organization details...</div>;

  if (isError) return <div>Error loading organization details...</div>;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update Organization Logo</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <div className="flex flex-col gap-4">
            <div>
              <label>Current Organization Logo</label>
              <div className="mt-2">
                {orgLogoUrl?.url ? (
                  <img
                    id="my-image"
                    src={orgLogoUrl.url} // Use the fetched URL here
                    alt="Organization Logo"
                    className="w-20 h-20 rounded-md"
                  />
                ) : (
                  <div className="w-20 h-20 bg-gray-200 rounded-md flex items-center justify-center">
                    No Logo
                  </div>
                )}
              </div>
            </div>

            <div>
              <label>Upload New Logo</label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setLogoFile(e.target.files ? e.target.files[0] : null)}
              />
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={updateOrgLogo.isPending}
          >
            Cancel
          </Button>
          <Button
            className="ml-2"
            onClick={handleSubmit}
            disabled={updateOrgLogo.isPending}
          >
            {updateOrgLogo.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              'Update Logo'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
